/**
 * M9 CH-4: the hulk survey card (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §3.3, §4.2; economy direction
 * §2.7). The survey is the wreck scan on a hulk with Scanner Mk II+ (`hulks.json survey.minScannerTier`; the live
 * hook is CH-12a's). It reads the hull class, 1..3 named flaws (`flaws.ts`, by hash), a seeded First Rush hulk's
 * pattern edge, the claimed value, hazards and squatters (by region, by hash) and the kit list, and records the
 * survey in `career.hulks.sites[key]` (`surveyed`, `flaws`).
 *
 * - **The kit** (`hulkKit`): ceil(`kit.share` × each **claim-cost** ware) plus `kit.fuelCells[hull]` fuel cells. The
 *   claim cost is the component-tier part of `buildCost` (`flaws.ts claimCostOf`, the kit rule T52), so a refined or
 *   import ware in a build cost never enters a kit. Goods only: no credits, no tetherstone (a `GoodId` is never raw).
 * - **The card model** (`hulkCardModel`) is in the shape of M7's `surveyCardModel`: a title, the text lines and
 *   `SurveyKitRow`s with delivered / needed and what the hold carries.
 *
 * Career in, Career out; pure. Survey time only, never per frame.
 */
import { GOOD_NAMES } from '../career/ops'
import { goodT, type GoodId } from '../career/parts'
import type { Career, HulkSite, HulkState } from '../career/types'
import type { RegionId } from '../galaxy/types'
import { hash32 } from '../space/hash'
import type { KitRequirement } from '../kits/deliver'
import type { SurveyKitRow } from '../story-link/reactivation/survey'
import { fillTokens } from '../story-link/world'
import { hulksConfig, type HulkEdge, type HulkFlaw, type HulksConfig } from './data'
import { buildCostOf, claimCostOf, edgeOf, flawsOf, hulkTraits, valueMultOf } from './flaws'

const SALT_COOLANT = 0x48434f4c // 'HCOL'
const SALT_SQUAT = 0x48535154 // 'HSQT'
const U32 = 4294967296

/** What the survey needs to know about one hulk (a seeded `HulkSpot` or an off-screen wreck's hulk). */
export interface HulkRef {
  /** `hk:<sys>:<n>` (seeded First Rush) or `ow-<seq>` (an off-screen combat loss). */
  readonly key: string
  /** Strategic `hulls.json` id. */
  readonly hull: string
  /** The hulk's own seed (the flaw and hazard rolls hash it). */
  readonly seed: number
  /** The region of the hulk's system (hazards and squatters). */
  readonly region: RegionId
}

/** A hulk's hazards and squatters (by hash of its seed, shares by region). */
export interface HulkHazards {
  readonly coolantHazard: boolean
  readonly squatters: boolean
  /** Credits that pay the squat off (0 without squatters). */
  readonly fenceFee: number
}

export interface HulkSurveyCard {
  readonly key: string
  readonly hull: string
  /** The flaws bitmask (flaws and the FR edge), as `HulkSite.flaws` stores it. */
  readonly flaws: number
  readonly flawList: readonly HulkFlaw[]
  readonly edge: HulkEdge | null
  /** The claimed hull's value multiplier (CH-8). */
  readonly valueMult: number
  readonly hazards: HulkHazards
  readonly kit: KitRequirement
}

export type HulkSurveyRefusal = 'scanner' | 'notEligible'

export type HulkSurveyOutcome = { ok: false; reason: HulkSurveyRefusal; text: string } | { ok: true; card: HulkSurveyCard; career: Career }

/** A claimable hull: in `eligibleHulls`, with a build cost (small and medium player-class hulls only). */
export function hulkEligible(hull: string, cfg: HulksConfig = hulksConfig()): boolean {
  return Object.hasOwn(cfg.eligibleHulls, hull) && buildCostOf(hull) !== null
}

/** The kit that makes a `hull` flightworthy: ceil(share × each claim-cost ware) + fuel cells (T52). Goods only. */
export function hulkKit(hull: string, cfg: HulksConfig = hulksConfig()): KitRequirement {
  const goods: Partial<Record<GoodId, number>> = {}
  const cost = claimCostOf(hull)
  if (cost !== null) {
    for (const [g, n] of Object.entries(cost) as [GoodId, number | undefined][]) {
      if (n !== undefined && n > 0) goods[g] = Math.ceil(n * cfg.kit.share - 1e-9)
    }
  }
  const cells = cfg.kit.fuelCells[hull] ?? 0
  if (cells > 0) goods.fuel_cells = (goods.fuel_cells ?? 0) + cells
  return { components: {}, goods }
}

/** A hulk's hazards and squatters. */
export function hulkHazards(ref: HulkRef, cfg: HulksConfig = hulksConfig()): HulkHazards {
  const h = cfg.hazards
  const coolantHazard = hash32(ref.seed, SALT_COOLANT) / U32 < h.coolantShare[ref.region]
  const squatters = hash32(ref.seed, SALT_SQUAT) / U32 < h.squatterShare[ref.region]
  return { coolantHazard, squatters, fenceFee: squatters ? h.fenceFee[ref.region] : 0 }
}

/** The survey card of a claimable hulk (pure; no Career). */
export function surveyHulk(ref: HulkRef, cfg: HulksConfig = hulksConfig()): HulkSurveyCard {
  const flaws = hulkTraits(ref.key, ref.seed, ref.hull, cfg)
  return {
    key: ref.key,
    hull: ref.hull,
    flaws,
    flawList: flawsOf(flaws, cfg),
    edge: edgeOf(flaws, cfg),
    valueMult: valueMultOf(flaws, cfg),
    hazards: hulkHazards(ref, cfg),
    kit: hulkKit(ref.hull, cfg),
  }
}

/**
 * The Mk II+ survey of a hulk: refused below `survey.minScannerTier` (M7's refusal pattern) or for a hull that is
 * not claimable or a hulk already finished; else the card and the Career with the survey recorded (`surveyed` at the
 * first survey's game time, `flaws`; a re-survey changes nothing, the roll being a hash).
 */
export function hulkSurvey(ref: HulkRef, career: Career, scannerTier: number, cfg: HulksConfig = hulksConfig()): HulkSurveyOutcome {
  if (scannerTier < cfg.survey.minScannerTier) return { ok: false, reason: 'scanner', text: cfg.text.refusals.scanner }
  const state: HulkState = career.hulks ?? { done: [], sites: {} }
  if (!hulkEligible(ref.hull, cfg) || state.done.includes(ref.key)) {
    return { ok: false, reason: 'notEligible', text: cfg.text.refusals.notEligible }
  }
  const card = surveyHulk(ref, cfg)
  const prev: HulkSite | undefined = state.sites[ref.key]
  if (prev?.surveyed !== undefined && prev.flaws === card.flaws && prev.hull === ref.hull) return { ok: true, card, career }
  const site: HulkSite = { ...prev, hull: ref.hull, surveyed: prev?.surveyed ?? career.gameTime, flaws: card.flaws }
  return { ok: true, card, career: { ...career, hulks: { done: state.done, sites: { ...state.sites, [ref.key]: site } } } }
}

// ---------------------------------------------------------------------------------------------
// The card model (text UI, the shape of M7's `surveyCardModel`)
// ---------------------------------------------------------------------------------------------

export interface HulkCardModel {
  key: string
  title: string
  /** The card's lines in order, the kit table included. */
  lines: string[]
  kit: SurveyKitRow[]
}

const signedPct = (mult: number): string => {
  const p = Math.round((mult - 1) * 100)
  return p > 0 ? `+${p}` : String(p)
}

/** The kit table: what the kit needs, what this hulk's site has had delivered and what the hold carries. */
export function hulkKitRows(card: HulkSurveyCard, career: Career): SurveyKitRow[] {
  const delivered = career.hulks?.sites[card.key]?.kit
  const rows: SurveyKitRow[] = []
  for (const [g, need] of Object.entries(card.kit.goods) as [GoodId, number | undefined][]) {
    if (need === undefined || need <= 0) continue
    rows.push({
      kind: 'good',
      id: g,
      label: GOOD_NAMES[g],
      need,
      delivered: delivered?.goods[g] ?? 0,
      inHold: Math.floor(goodT(career.cargo.parts, g) + 1e-9),
      band: null,
      holdBest: null,
    })
  }
  return rows
}

/** The survey card's text UI, with the site's progress (vented, cleared, kit) and the hold's contents. */
export function hulkCardModel(card: HulkSurveyCard, career: Career, cfg: HulksConfig = hulksConfig()): HulkCardModel {
  const t = cfg.text
  const site = career.hulks?.sites[card.key]
  const title = fillTokens(t.title, { hull: t.hulls[card.hull] ?? card.hull })
  // PF-2: the First Rush edge is a bonus, so it has its own line before the flaws (never under the "Flaws:" header).
  const lines: string[] = []
  if (card.edge !== null) lines.push(fillTokens(t.edge, { name: card.edge.name, stat: t.stats[card.edge.stat], change: signedPct(card.edge.mult) }))
  lines.push(card.flawList.length > 0 ? t.flawsHeader : t.flawsNone)
  for (const f of card.flawList) lines.push(fillTokens(t.flawRow, { name: f.name, stat: t.stats[f.stat], change: signedPct(f.mult) }))
  lines.push(fillTokens(t.value, { pct: String(Math.round(card.valueMult * 100)) }))
  if (card.hazards.coolantHazard) lines.push(site?.vented === true ? t.coolantVented : t.coolant)
  if (card.hazards.squatters) lines.push(site?.cleared !== undefined ? t.squatCleared : fillTokens(t.squatters, { fee: String(card.hazards.fenceFee) }))
  const kit = hulkKitRows(card, career)
  lines.push(t.kitHeader)
  for (const r of kit) {
    lines.push(fillTokens(t.goodRow, { label: r.label, delivered: String(r.delivered), need: String(r.need), inHold: String(r.inHold) }))
  }
  return { key: card.key, title, lines, kit }
}
